import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, UserCheck, Users, UserX, UserMinus, KeyRound, UserPlus, Eye, EyeOff, Download, Upload } from 'lucide-react';
import { useUserStore, useToastStore, useAuthStore, useShiftStore } from '../store';
import { Spinner, EmptyState, Badge, Avatar, Modal } from '../components/ui';
import { api } from '../utils/api';

type TabType = 'pending' | 'active' | 'inactive';

export function AdminUsersPage() {
  const navigate = useNavigate();
  const { pendingUsers, users, isLoading, fetchPendingUsers, fetchUsers } = useUserStore();
  const { fetchShifts } = useShiftStore();
  const { addToast } = useToastStore();
  const { user: currentUser } = useAuthStore();
  const isSuperAdmin = currentUser?.role === 'SuperAdmin';
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('active');
  
  // Deactivate modal state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState<{ id: string; name: string } | null>(null);
  
  // Reset password modal state
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [userToReset, setUserToReset] = useState<{ id: string; name: string } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Create user modal state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    username: '',
    email: '',
    password: '',
    display_name: '',
    role: 'Employee' as 'Admin' | 'Employee',
  });
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isExportingTenants, setIsExportingTenants] = useState(false);
  const [isImportingTenants, setIsImportingTenants] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tenantFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchPendingUsers();
    fetchUsers();
  }, []);

  const handleApprove = async (userId: string) => {
    setProcessingId(userId);
    const result = await api.approveUser(userId);
    setProcessingId(null);

    if (result.success) {
      addToast('User approved!', 'success');
      fetchPendingUsers();
      fetchUsers();
    } else {
      addToast(result.error || 'Failed to approve user', 'error');
    }
  };

  const handleReject = async (userId: string) => {
    setProcessingId(userId);
    const result = await api.rejectUser(userId);
    setProcessingId(null);

    if (result.success) {
      addToast('User rejected', 'success');
      fetchPendingUsers();
    } else {
      addToast(result.error || 'Failed to reject user', 'error');
    }
  };

  const confirmDeactivate = (userId: string, name: string) => {
    setUserToDeactivate({ id: userId, name });
    setShowDeactivateModal(true);
  };

  const handleDeactivate = async () => {
    if (!userToDeactivate) return;

    setProcessingId(userToDeactivate.id);
    const result = await api.deactivateUser(userToDeactivate.id);
    setProcessingId(null);
    setShowDeactivateModal(false);
    setUserToDeactivate(null);

    if (result.success) {
      addToast('User deactivated', 'success');
      fetchUsers();
    } else {
      addToast(result.error || 'Failed to deactivate user', 'error');
    }
  };

  const handleReactivate = async (userId: string) => {
    setProcessingId(userId);
    const result = await api.reactivateUser(userId);
    setProcessingId(null);

    if (result.success) {
      addToast('User reactivated!', 'success');
      fetchUsers();
    } else {
      addToast(result.error || 'Failed to reactivate user', 'error');
    }
  };


  const openResetPasswordModal = (userId: string, name: string) => {
    setUserToReset({ id: userId, name });
    setNewPassword('');
    setShowPassword(false);
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!userToReset || !newPassword) return;

    if (newPassword.length < 6) {
      addToast('Password must be at least 6 characters', 'warning');
      return;
    }

    setProcessingId(userToReset.id);
    const result = await api.adminResetPassword(userToReset.id, newPassword);
    setProcessingId(null);

    if (result.success) {
      addToast('Password reset successfully', 'success');
      setShowResetPasswordModal(false);
      setUserToReset(null);
      setNewPassword('');
    } else {
      addToast(result.error || 'Failed to reset password', 'error');
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setShowPassword(true);
  };

  const handleCreateUser = async () => {
    if (!createUserForm.username || !createUserForm.email || !createUserForm.password) {
      addToast('Please fill in all required fields', 'warning');
      return;
    }

    if (createUserForm.password.length < 6) {
      addToast('Password must be at least 6 characters', 'warning');
      return;
    }

    setProcessingId('create');
    const result = await api.adminCreateUser(createUserForm);
    setProcessingId(null);

    if (result.success) {
      addToast('User created successfully', 'success');
      setShowCreateUserModal(false);
      setCreateUserForm({
        username: '',
        email: '',
        password: '',
        display_name: '',
        role: 'Employee',
      });
      fetchUsers();
    } else {
      addToast(result.error || 'Failed to create user', 'error');
    }
  };

  const handleExport = async () => {
    if (isExporting) return;
    
    setIsExporting(true);
    try {
      const result = await api.exportData();
      if (result.success) {
        addToast('Data exported successfully!', 'success');
      } else {
        addToast(result.error || 'Failed to export data', 'error');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportClick = () => {
    if (isImporting) return;
    fileInputRef.current?.click();
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) return;

    const file = event.target.files[0];
    setIsImporting(true);

    try {
      const fileContent = await file.text();
      const importData = JSON.parse(fileContent);

      if (!importData.data) {
        addToast('Invalid import file format', 'error');
        setIsImporting(false);
        return;
      }

      const tenantSlugs = (importData.data.tenants || [])
        .map((t: { slug?: string }) => t.slug)
        .filter(Boolean)
        .join(', ');
      const confirmMessage = isSuperAdmin
        ? (tenantSlugs
          ? `This will replace all data for tenant(s): ${tenantSlugs}. Continue?`
          : 'This will replace data for all tenant slugs found in the backup file. Continue?')
        : `This will replace all data for your organization${tenantSlugs ? ` (${tenantSlugs})` : ''}. Other organizations will not be affected. Continue?`;
      if (!window.confirm(confirmMessage)) {
        setIsImporting(false);
        return;
      }

      const result = await api.importData(importData.data, importData.version);

      if (result.success) {
        const summary = (result as {
          summary?: {
            users?: number;
            usersCreated?: number;
            usersUpdated?: number;
            usersUnchanged?: number;
            shifts?: number;
          };
        }).summary;
        const userParts = [
          summary?.usersCreated ? `${summary.usersCreated} created` : null,
          summary?.usersUpdated ? `${summary.usersUpdated} updated` : null,
          summary?.usersUnchanged ? `${summary.usersUnchanged} unchanged` : null,
        ].filter(Boolean);
        const userSummary = userParts.length > 0
          ? userParts.join(', ')
          : `${summary?.users || 0} users`;
        addToast(
          `Data imported successfully! (${userSummary}, ${summary?.shifts || 0} shifts)`,
          'success'
        );
        // Refresh all data after import
        await Promise.all([
          fetchPendingUsers(),
          fetchUsers(),
          fetchShifts()
        ]);
      } else {
        addToast(result.error || 'Failed to import data', 'error');
      }
    } catch (error) {
      addToast('Failed to read import file', 'error');
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportTenants = async () => {
    setIsExportingTenants(true);
    try {
      const result = await api.exportTenants();
      if (result.success && result.data) {
        // Download the JSON file
        const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tenants-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        addToast('Tenants exported successfully', 'success');
      } else {
        addToast(result.error || 'Failed to export tenants', 'error');
      }
    } catch (error) {
      addToast('Failed to export tenants', 'error');
    } finally {
      setIsExportingTenants(false);
    }
  };

  const handleImportTenants = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsImportingTenants(true);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      
      // Extract tenants from export file
      const tenants = json.data?.tenants || (Array.isArray(json) ? json : []);
      
      if (tenants.length === 0) {
        addToast('No tenants found in file', 'error');
        return;
      }
      
      const result = await api.importTenants(tenants);
      if (result.success) {
        addToast(result.message || 'Tenants imported successfully', 'success');
      } else {
        addToast(result.error || 'Failed to import tenants', 'error');
      }
    } catch (error) {
      addToast('Failed to parse file', 'error');
    } finally {
      setIsImportingTenants(false);
      if (tenantFileInputRef.current) {
        tenantFileInputRef.current.value = '';
      }
    }
  };

  const activeUsers = (users && Array.isArray(users)) ? users.filter(u => u.status === 'Active') : [];
  const inactiveUsers = (users && Array.isArray(users)) ? users.filter(u => u.status === 'Inactive' || u.status === 'Rejected') : [];

  const tabs = [
    { id: 'active' as TabType, label: 'Active Users', count: activeUsers.length },
    { id: 'pending' as TabType, label: 'Pending', count: (pendingUsers && Array.isArray(pendingUsers)) ? pendingUsers.length : 0 },
    { id: 'inactive' as TabType, label: 'Inactive', count: inactiveUsers.length },
  ];

  if (isLoading && (!users || !Array.isArray(users) || users.length === 0)) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
          <p className="text-slate-500 mt-1">Manage user registrations and access</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="btn btn-secondary"
            title={isSuperAdmin ? 'Export all data (or one tenant via API)' : 'Export your organization data'}
          >
            {isExporting ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span className="ml-1">{isSuperAdmin ? 'Export' : 'Export Org'}</span>
          </button>
          <button
            onClick={handleImportClick}
            disabled={isImporting}
            className="btn btn-secondary"
            title={isSuperAdmin ? 'Import data for any tenant in the file' : 'Import data for your organization only'}
          >
            {isImporting ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            <span className="ml-1">{isSuperAdmin ? 'Import' : 'Import Org'}</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImport}
            className="hidden"
          />
          {currentUser?.role === 'SuperAdmin' && (
            <>
              <div className="h-6 w-px bg-slate-300 mx-1" />
              <button
                onClick={handleExportTenants}
                disabled={isExportingTenants}
                className="btn btn-secondary"
                title="Export tenants (SuperAdmin only)"
              >
                {isExportingTenants ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="ml-1">Export Tenants</span>
              </button>
              <button
                onClick={() => tenantFileInputRef.current?.click()}
                disabled={isImportingTenants}
                className="btn btn-secondary"
                title="Import tenants (SuperAdmin only)"
              >
                {isImportingTenants ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                <span className="ml-1">Import Tenants</span>
              </button>
              <input
                ref={tenantFileInputRef}
                type="file"
                accept=".json"
                onChange={handleImportTenants}
                className="hidden"
              />
            </>
          )}
          <button onClick={() => setShowCreateUserModal(true)} className="btn btn-primary">
            <UserPlus className="w-4 h-4" />
            Create User
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-600'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Active Users Tab */}
      {activeTab === 'active' && (
        activeUsers.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={<Users className="w-12 h-12 mx-auto" />}
              title="No active users"
              description="Create a user to get started."
              action={
                <button onClick={() => setShowCreateUserModal(true)} className="btn btn-primary">
                  <UserPlus className="w-4 h-4" />
                  Create User
                </button>
              }
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">User</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Role</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeUsers.map(user => (
                  <tr key={user.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/users/${user.id}`)}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={user.display_name || user.username} size="sm" />
                        <span className="font-medium text-slate-900">
                          {user.display_name || user.username}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{user.email}</td>
                    <td className="px-6 py-4">
                      <Badge variant={user.role === 'Admin' ? 'purple' : 'info'}>
                        {user.role}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="success">Active</Badge>
                    </td>
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => openResetPasswordModal(user.id, user.display_name || user.username)}
                          className="btn btn-ghost btn-sm text-slate-600 hover:bg-slate-100"
                          title="Reset Password"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => confirmDeactivate(user.id, user.display_name || user.username)}
                          disabled={processingId === user.id}
                          className="btn btn-ghost btn-sm text-red-600 hover:bg-red-50"
                          title="Deactivate User"
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Pending Users Tab */}
      {activeTab === 'pending' && (
        pendingUsers.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={<UserCheck className="w-12 h-12 mx-auto" />}
              title="No pending registrations"
              description="All user registrations have been processed."
            />
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pendingUsers.map(user => (
              <div key={user.id} className="card p-6">
                <div className="flex items-start gap-4">
                  <Avatar name={user.display_name || user.username} size="lg" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">
                      {user.display_name || user.username}
                    </h3>
                    <p className="text-slate-500 text-sm truncate">{user.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={user.role === 'Admin' ? 'purple' : 'info'}>
                        {user.role}
                      </Badge>
                      <Badge variant="warning">Pending</Badge>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleReject(user.id)}
                    disabled={processingId === user.id}
                    className="btn btn-danger btn-sm flex-1"
                  >
                    {processingId === user.id ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <X className="w-4 h-4" />
                        Reject
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleApprove(user.id)}
                    disabled={processingId === user.id}
                    className="btn btn-success btn-sm flex-1"
                  >
                    {processingId === user.id ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Approve
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Inactive Users Tab */}
      {activeTab === 'inactive' && (
        inactiveUsers.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={<UserX className="w-12 h-12 mx-auto" />}
              title="No inactive users"
              description="There are no deactivated or rejected users."
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">User</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Role</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {inactiveUsers.map(user => (
                  <tr key={user.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/users/${user.id}`)}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={user.display_name || user.username} size="sm" />
                        <span className="font-medium text-slate-900">
                          {user.display_name || user.username}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{user.email}</td>
                    <td className="px-6 py-4">
                      <Badge variant={user.role === 'Admin' ? 'purple' : 'info'}>
                        {user.role}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="danger">{user.status}</Badge>
                    </td>
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleReactivate(user.id)}
                        disabled={processingId === user.id}
                        className="btn btn-ghost btn-sm text-emerald-600 hover:bg-emerald-50"
                      >
                        {processingId === user.id ? (
                          <div className="w-4 h-4 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin" />
                        ) : (
                          <>
                            <Check className="w-4 h-4" />
                            Reactivate
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Deactivate Confirmation Modal */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => {
          setShowDeactivateModal(false);
          setUserToDeactivate(null);
        }}
        title="Deactivate User"
      >
        <div className="space-y-4">
          <p className="text-slate-600">
            Are you sure you want to deactivate <strong>{userToDeactivate?.name}</strong>?
          </p>
          <p className="text-sm text-slate-500">
            The user will no longer be able to log in or be assigned shifts. You can reactivate them later.
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowDeactivateModal(false);
                setUserToDeactivate(null);
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeactivate}
              disabled={processingId === userToDeactivate?.id}
              className="btn btn-danger"
            >
              {processingId === userToDeactivate?.id ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Deactivate User'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={showResetPasswordModal}
        onClose={() => {
          setShowResetPasswordModal(false);
          setUserToReset(null);
          setNewPassword('');
        }}
        title="Reset Password"
      >
        <div className="space-y-4">
          <p className="text-slate-600">
            Reset password for <strong>{userToReset?.name}</strong>
          </p>
          
          <div>
            <label className="input-label">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input pr-20"
                placeholder="Enter new password"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-1.5 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          
          <button
            type="button"
            onClick={generateRandomPassword}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Generate random password
          </button>

          <p className="text-sm text-slate-500">
            Make sure to share this password securely with the user.
          </p>

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowResetPasswordModal(false);
                setUserToReset(null);
                setNewPassword('');
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleResetPassword}
              disabled={processingId === userToReset?.id || !newPassword}
              className="btn btn-primary"
            >
              {processingId === userToReset?.id ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Reset Password'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Create User Modal */}
      <Modal
        isOpen={showCreateUserModal}
        onClose={() => {
          setShowCreateUserModal(false);
          setCreateUserForm({
            username: '',
            email: '',
            password: '',
            display_name: '',
            role: 'Employee',
          });
        }}
        title="Create User"
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="input-label">Username *</label>
              <input
                type="text"
                value={createUserForm.username}
                onChange={(e) => setCreateUserForm({ ...createUserForm, username: e.target.value })}
                className="input"
                placeholder="johndoe"
              />
            </div>
            <div>
              <label className="input-label">Display Name</label>
              <input
                type="text"
                value={createUserForm.display_name}
                onChange={(e) => setCreateUserForm({ ...createUserForm, display_name: e.target.value })}
                className="input"
                placeholder="John Doe"
              />
            </div>
          </div>

          <div>
            <label className="input-label">Email *</label>
            <input
              type="email"
              value={createUserForm.email}
              onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
              className="input"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label className="input-label">Password *</label>
            <div className="relative">
              <input
                type={showCreatePassword ? 'text' : 'password'}
                value={createUserForm.password}
                onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                className="input pr-20"
                placeholder="Minimum 6 characters"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                <button
                  type="button"
                  onClick={() => setShowCreatePassword(!showCreatePassword)}
                  className="p-1.5 text-slate-400 hover:text-slate-600"
                >
                  {showCreatePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
                let password = '';
                for (let i = 0; i < 12; i++) {
                  password += chars.charAt(Math.floor(Math.random() * chars.length));
                }
                setCreateUserForm({ ...createUserForm, password });
                setShowCreatePassword(true);
              }}
              className="text-sm text-primary-600 hover:text-primary-700 mt-1"
            >
              Generate random password
            </button>
          </div>

          <div>
            <label className="input-label">Role *</label>
            <select
              value={createUserForm.role}
              onChange={(e) => setCreateUserForm({ ...createUserForm, role: e.target.value as 'Admin' | 'Employee' })}
              className="input"
            >
              <option value="Employee">Employee</option>
              <option value="Admin">Admin</option>
            </select>
          </div>

          <p className="text-sm text-slate-500">
            The user will be created with Active status and can log in immediately.
          </p>

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowCreateUserModal(false);
                setCreateUserForm({
                  username: '',
                  email: '',
                  password: '',
                  display_name: '',
                  role: 'Employee',
                });
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateUser}
              disabled={processingId === 'create' || !createUserForm.username || !createUserForm.email || !createUserForm.password}
              className="btn btn-primary"
            >
              {processingId === 'create' ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Create User'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
